import React, { useState, useEffect } from 'react';
import { X, FileText, AlertTriangle, Info } from 'lucide-react';
import { supabaseLeaseLotService, LeaseLot, ErrorReason } from '../../services/supabase/lease-lots';
import { TestSublease } from '../../types/test-sublease';

interface ViewSubLeaseLotsProps {
  isOpen: boolean;
  onClose: () => void;
  sublease: TestSublease;
}

export default function ViewSubLeaseLots({ isOpen, onClose, sublease }: ViewSubLeaseLotsProps) {
  const [lots, setLots] = useState<LeaseLot[]>([]);
  const [hasError, setHasError] = useState(false);
  const [errorReason, setErrorReason] = useState<ErrorReason>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  // Function to load the lots data for a sublease
  const loadLotsData = () => {
    if (!sublease) return;
    
    setIsLoading(true);
    setRequestError(null);
    setHasError(false);
    setErrorMessage("");
    
    supabaseLeaseLotService.getByLeaseId(Number(sublease.lease_id))
      .then(result => {
        setLots(result.lots);
        setHasError(result.error);
        setErrorReason(result.errorReason);
        setErrorMessage(result.errorMessage);
        setIsLoading(false);
      })
      .catch(err => {
        console.error('Error fetching sublease lots:', err);
        setRequestError('Failed to load lots data. Please try again later.');
        setIsLoading(false);
      });
  };

  // Load lots data when the modal is opened
  useEffect(() => {
    if (isOpen && sublease) {
      loadLotsData();
    }
  }, [isOpen, sublease]);

  // Function to get detailed error explanation based on the error reason
  const getDetailedErrorMessage = () => {
    switch (errorReason) {
      case 'sublease-not-found':
        return "The sublease record couldn't be found in the database. This may indicate a data integrity issue.";
      case 'no-lots-found':
        return "This sublease exists in the database but doesn't have any lots associated with it yet.";
      case 'query-error':
        return "There was an error querying the database. This could be due to connectivity issues or database permissions.";
      case 'not-authenticated':
        return "You need to be logged in to view this data. Please try logging in again.";
      default:
        return "An unexpected error occurred. Please try again or contact your system administrator.";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <FileText className="h-5 w-5 mr-2 text-primary" />
              <h2 className="text-2xl font-bold text-gray-800">
                Lots for Sublease #{sublease.lease_id}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Description of the relationship chain */}
          <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-md">
            <div className="flex items-start">
              <Info className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Database Relationship Chain</p>
                
                <p className="text-sm mt-1">
                  This view shows the lots associated with sublease #{sublease.lease_id}, including community information when available.
                </p>
              </div>
            </div>
          </div>

          {/* Error from the API request itself */}
          {requestError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {requestError}
            </div>
          )}

          {/* Data not available message */}
          {hasError && (
            <div className="mb-4 p-3 bg-yellow-100 text-yellow-700 rounded-md">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Data not available</p>
                  <p>{errorMessage}</p>
                  <p className="text-sm mt-1">{getDetailedErrorMessage()}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading spinner */}
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : lots.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No lots data available for this sublease.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lot ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Community
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Permitted Use
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lots.map((lot) => (
                    <tr key={lot.leaselot_lot}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {lot.lot_id || lot.leaselot_lot}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lot.community_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {lot.leaselot_use || lot.lot_perm_use || 'Not specified'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {lot.lot_desc || 'No description'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
